"use client";

import { useState, useEffect } from "react";
import { Transition } from "@headlessui/react";
import { CheckCircle, AlertCircle, X, Info } from "lucide-react";

let triggerToast;

export function toast({ type = "success", title, message, duration = 5000 }) {
  if (triggerToast) triggerToast({ type, title, message, duration });
}

export default function Toast() {
  const [toast, setToast] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
    duration: 5000,
  });

  useEffect(() => {
    triggerToast = ({ type, title, message, duration }) => {
      setToast({ show: true, type, title, message, duration });
    };
    return () => {
      triggerToast = null;
    };
  }, []);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(
        () => setToast((n) => ({ ...n, show: false })),
        toast.duration,
      );
      return () => clearTimeout(timer);
    }
  }, [toast]);

  if (!toast.show) return null;

  const icon =
    toast.type === "success" ? (
      <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
    ) : toast.type === "info" ? (
      <Info className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
    ) : (
      <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
    );

  return (
    <div
      aria-live="assertive"
      className="fixed inset-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6 pointer-events-none"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition
          show={toast.show}
          enter="transform transition ease-out duration-300"
          enterFrom="translate-x-full opacity-0"
          enterTo="translate-x-0 opacity-100"
          leave="transform transition ease-in duration-200"
          leaveFrom="translate-x-0 opacity-100"
          leaveTo="translate-x-full opacity-0"
        >
          <div className="pointer-events-auto w-full max-w-sm rounded-xl border bg-background text-foreground shadow-lg ring-1 ring-border">
            <div className="p-4 flex items-start gap-3">
              <div className="shrink-0">{icon}</div>
              <div className="flex-1">
                <p className="text-sm font-medium">{toast.title}</p>
                {toast.message && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {toast.message}
                  </p>
                )}
              </div>
              <button
                onClick={() => setToast((n) => ({ ...n, show: false }))}
                className="ms-2 rounded-md text-muted-foreground hover:text-foreground"
              >
                <span className="sr-only">Close</span>
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
}
